import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.js';
import { browserCaptureSeams, supportsCapture } from './voice/browser-capture.js';
import { currentSession, serverSttClient } from './voice/server-stt.js';
import { createPressToTalk } from './voice/press-to-talk.js';
import { browserLiveSeams } from './voice/live-seams.js';
// The redesign's faces (canvas handoff, 2026-09-11), self-hosted — no CDN: IBM Plex Sans Arabic
// carries the interface at 400/500/600; Scheherazade New is scripture only; IBM Plex Mono is
// Latin labels only.
import '@fontsource/ibm-plex-sans-arabic/400.css';
import '@fontsource/ibm-plex-sans-arabic/500.css';
import '@fontsource/ibm-plex-sans-arabic/600.css';
// 700 joined the set when the founder asked for the bolder type back (2026-09-13). Without the
// face, a display weight of 700 is synthesised by the browser, which thickens Arabic letterforms
// unevenly and thins their joins — a drawn weight is the only honest way to ask for one.
import '@fontsource/ibm-plex-sans-arabic/700.css';
import '@fontsource/scheherazade-new/400.css';
import '@fontsource/scheherazade-new/500.css';
import '@fontsource/ibm-plex-mono/400.css';
import './styles.css';
import { configuredKey, createAnalytics } from './analytics.js';
import { preferences } from './preferences.js';

/** Browser entry point. The API base URL comes from the development environment, never from a secret. */
const container = document.getElementById('root');
if (container === null) throw new Error('root container missing');

const configured: unknown = import.meta.env['VITE_BILAL_API_URL'];
// An explicitly empty value means same-origin: the deployed site serves the app and proxies the
// API on one https origin, which is also what the __Host- cookie requires. Unset falls back to
// the local development split (app on 5173, API on 3001).
const baseUrl = typeof configured === 'string' ? configured.trim() : 'http://127.0.0.1:3001';

/**
 * Product analytics, keyed from the build environment and inert without keys. Nothing loads and
 * nothing is sent until the person has said yes (`preferences.analyticsConsent`).
 */
const analytics = createAnalytics({
  amplitudeApiKey: configuredKey(import.meta.env['VITE_AMPLITUDE_API_KEY']),
  tiktokPixelId: configuredKey(import.meta.env['VITE_TIKTOK_PIXEL_ID']),
  // On unless turned off: nothing chosen resolves to granted (founder, 2026-09-14).
  consent: () => (preferences.analyticsOn() ? 'granted' : 'denied'),
});

// Arrival is itself the measurement that was missing: the pixel loads here rather than in the
// instant a conversion fires, so a visit is reported and the two conversions have traffic behind
// them. It stays behind the same switch — a browser that turned measurement off installs nothing.
analytics.start();

/**
 * No microphone control at all when the browser cannot record, rather than one that fails when pressed.
 * The recording goes to Bilal's own API, which forwards it to the speech provider over the documented
 * plain-HTTP endpoint; no provider credential ever reaches the browser.
 */
const capture = supportsCapture({
  MediaRecorder: globalThis.MediaRecorder,
  mediaDevices: globalThis.navigator.mediaDevices,
})
  ? createPressToTalk(
      browserCaptureSeams({
        stt: serverSttClient({
          baseUrl,
          fetchImpl: (input, init) => globalThis.fetch(input, init),
          sessionId: () => currentSession.id,
        }),
        mediaDevices: globalThis.navigator.mediaDevices,
        recorderFor: (stream) => new MediaRecorder(stream),
      }),
    )
  : undefined;

createRoot(container).render(
  <StrictMode>
    <App
      fetchImpl={(input, init) => globalThis.fetch(input, init)}
      baseUrl={baseUrl}
      capture={capture}
      liveSeams={browserLiveSeams(baseUrl)}
      analytics={analytics}
    />
  </StrictMode>,
);
